import { useRef } from 'react';
import { Form, Spin, Radio, Divider } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { download_file } from "../../services/apiCallerService/GraphqlCaller";

export default function PathSelector({ loading, bucketsWithFiles, selectedPath, on_path_change, token }) {

  const form = useRef();

  // Radio.Group uses === equality on its value prop — objects created inline fail this on every render.
  // Use the stable pathId string as the radio value and reconstruct the full object on change.
  const handlePathChange = (pathId) => {
    for (const bucket of bucketsWithFiles) {
      const path = bucket.paths.find((p) => p.pathId === pathId);
      if (path) {
        on_path_change(makePathValue(bucket, path));
        return;
      }
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const downloadUrl = await download_file(token, fileId);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert(`Failed to download file: ${error.message || "Unknown error"}`);
    }
  };

  const makePathValue = (bucket, path) => ({
    pathId: path.pathId,
    bucket: bucket.bucketName,
    bucketId: bucket.bucketId,
    bucketName: bucket.bucketName,
    pathPrefix: path.pathPrefix,
    jiraTicket: bucket.jiraTicket,
    displayName: path.displayName,
    overwriteExistingFiles: false,
  });

  if (loading) {
    return <div><Spin /></div>;
  }

  if (!bucketsWithFiles || bucketsWithFiles.length === 0) {
    return <div>No upload destinations available.</div>;
  }

  return (
    <div>
      <Form ref={form}>
        <Radio.Group
          value={selectedPath?.pathId ?? null}
          onChange={(e) => handlePathChange(e.target.value)}
          className="path-selector-radio-group">
          {bucketsWithFiles.map((bucket) => {
            const isSinglePath = bucket.paths.length === 1;

            if (isSinglePath) {
              const path = bucket.paths[0];
              const pathDisplayName = path.displayName || path.pathPrefix;
              const compactLabel = `${pathDisplayName} (${bucket.bucketName} → ${path.pathPrefix})`;

              return (
                <div key={bucket.bucketId} className="path-single-bucket-card">
                  <Radio
                    value={path.pathId}
                    className={bucket.files.length > 0 ? "path-single-bucket-radio path-single-bucket-radio-with-files" : "path-single-bucket-radio"}>
                    <strong>{compactLabel}</strong>
                  </Radio>

                  {bucket.files.length > 0 && (
                    <div className="path-files-container">
                      <div className="path-files-label">Available files:</div>
                      <ul className="path-files-list">
                        {bucket.files.map((file) => (
                          <li key={file.fileId} className="path-file-item">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDownloadFile(file.fileId, file.fileDisplayName || file.fileKey || "file");
                              }}
                              className="path-file-download-btn"
                            >
                              <DownloadOutlined className="path-file-download-icon" />
                              {file.fileDisplayName || file.fileKey}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={bucket.bucketId} className="path-multi-bucket-card">
                <div className="path-bucket-header">
                  <h3 className="path-bucket-title">{bucket.bucketName}</h3>
                  {bucket.jiraTicket && (
                    <span className="path-bucket-jira">JIRA: {bucket.jiraTicket}</span>
                  )}
                </div>

                {bucket.files.length > 0 && (
                  <div className="path-files-section">
                    <div className="path-files-label-multi">Available files:</div>
                    <ul className="path-files-list">
                      {bucket.files.map((file) => (
                        <li key={file.fileId} className="path-file-item-multi">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDownloadFile(file.fileId, file.fileDisplayName || file.fileKey || "file");
                            }}
                            className="path-file-download-btn"
                          >
                            <DownloadOutlined className="path-file-download-icon" />
                            {file.fileDisplayName || file.fileKey}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Divider className="path-divider" />

                <div className="path-selection-label">Select upload destination:</div>
                {bucket.paths.map((path) => {
                  const pathLabel = path.displayName
                    ? `${path.displayName} (${path.pathPrefix})`
                    : path.pathPrefix;

                  return (
                    <div key={path.pathId} className="path-radio-item">
                      <Radio value={path.pathId}>
                        {pathLabel}
                      </Radio>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Radio.Group>
      </Form>
    </div>
  );
}
